import { AlertTriangle, CircleAlert, Info, ListChecks, type LucideIcon } from 'lucide-react';

/**
 * Shared between the header dropdown and the notifications page.
 *
 * In its own module rather than exported from `NotificationsPage`: the bell
 * lives in `AppShell`, so importing from the page would drag the lazily-loaded
 * page — and everything it imports — back into the entry bundle, which is the
 * opposite of what `lazy()` was there for.
 */

/** "3 dk önce" beats a timestamp for anything from the last day, which is when
 *  a notification is actually acted on. */
export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'az önce';
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} gün önce`;
  return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
}

const SEVERITY: Record<string, { Icon: LucideIcon; className: string }> = {
  critical: { Icon: CircleAlert, className: 'text-danger' },
  warning: { Icon: AlertTriangle, className: 'text-warning' },
  action_required: { Icon: ListChecks, className: 'text-primary' },
  info: { Icon: Info, className: 'text-fg-muted' },
};

export function severityVisual(severity: string): { Icon: LucideIcon; className: string } {
  return SEVERITY[severity] ?? SEVERITY.info!;
}

/**
 * Groups the list by day, then by month, without a date library.
 *
 * "Bugün" / "Dün" / "Daha önce" was right while the list was a hard `take: 50`
 * that never reached last month. Now that it pages back through a year, a
 * single "Daha önce" heading over four hundred rows is a heading that says
 * nothing — the reader scrolling for something from July has no landmark at
 * all. Everything older than yesterday falls into its own month instead, and
 * the year is added once the month is ambiguous.
 */
export function dayBucket(iso: string): string {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const when = new Date(iso);
  const t = when.getTime();
  if (t >= startOfToday) return 'Bugün';
  if (t >= startOfToday - 86_400_000) return 'Dün';
  // Same calendar year: the month alone is unambiguous and shorter to scan.
  // A different year needs it, or "Ağustos" appears twice with a year between.
  return when.toLocaleDateString('tr-TR', {
    month: 'long',
    ...(when.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}
